/* eslint-disable */
import Long from "long";
import _m0 from "protobufjs/minimal";
import { Vector2fPb } from "../../gen/eigen_lcm/Vector2f";
import { FrameType_Enum, frameType_EnumFromJSON, frameType_EnumToJSON } from "../../vehicle/skills/waypoints";

export const protobufPackage = "geofence";

/**
 * Defines a region or set of regions in space that the drone must stay inside or stay
 * outside of, which can be represented by 2.5D (min/max z) or 3D shapes.
 */
export interface Geofence {
  utime: number;
  /** Set of shapes defining the geofence */
  shapes: GeofenceShape[];
}

/** A shape making up all or part of the geofence */
export interface GeofenceShape {
  frame: FrameType_Enum;
  /**
   * Determines whether the drone will keep out (value = true) or keep in (value = false) of
   * this shape. By default, the drone will keep in.
   */
  keepOut: boolean;
  source: GeofenceShape_Source;
  metadata:
    | Metadata
    | undefined;
  /** 2.5D Shapes */
  cylinder?: Cylinder | undefined;
  poly2d?: Polygon | undefined;
}

export enum GeofenceShape_Source {
  UNKNOWN = 0,
  MAVLINK_PARAM = 1,
  MAVLINK_MISSION = 2,
  UNRECOGNIZED = -1,
}

export function geofenceShape_SourceFromJSON(object: any): GeofenceShape_Source {
  switch (object) {
    case 0:
    case "UNKNOWN":
      return GeofenceShape_Source.UNKNOWN;
    case 1:
    case "MAVLINK_PARAM":
      return GeofenceShape_Source.MAVLINK_PARAM;
    case 2:
    case "MAVLINK_MISSION":
      return GeofenceShape_Source.MAVLINK_MISSION;
    case -1:
    case "UNRECOGNIZED":
    default:
      return GeofenceShape_Source.UNRECOGNIZED;
  }
}

export function geofenceShape_SourceToJSON(object: GeofenceShape_Source): string {
  switch (object) {
    case GeofenceShape_Source.UNKNOWN:
      return "UNKNOWN";
    case GeofenceShape_Source.MAVLINK_PARAM:
      return "MAVLINK_PARAM";
    case GeofenceShape_Source.MAVLINK_MISSION:
      return "MAVLINK_MISSION";
    case GeofenceShape_Source.UNRECOGNIZED:
    default:
      return "UNRECOGNIZED";
  }
}

/** Metadata for geofence and geo-awareness zones */
export interface Metadata {
  id: string;
  country: string;
}

/**
 * Height limits (min/max z) for a 2.5D shape. If a *_enabled parameter is true, the relevant limit
 * will be adhered to, otherwise it will be ignored.
 */
export interface HeightLimits {
  floor: number;
  floorEnabled: boolean;
  ceiling: number;
  ceilingEnabled: boolean;
  frame: FrameType_Enum;
}

/** Cylinder with optional height limits */
export interface Cylinder {
  radius: number;
  center: Vector2fPb | undefined;
  heightLimits: HeightLimits | undefined;
}

/** 2D polygon with optional height limits. Uses a right-handed (counter-clockwise) winding order. */
export interface Polygon {
  vertices: Vector2fPb[];
  heightLimits: HeightLimits | undefined;
}

export interface ParsingError {
  error: ParsingError_Error;
  detail: string;
}

export enum ParsingError_Error {
  UNKNOWN = 0,
  /** MISSING_REQUIRED_FILED - The input is missing a filed that is required */
  MISSING_REQUIRED_FILED = 1,
  /** FAILED_TO_PARSE_REQUIRED_VALUE - Unable to parse a value e.g. because the type does not match */
  FAILED_TO_PARSE_REQUIRED_VALUE = 2,
  /** UNRECOGNIZED_ENUM_VALUE - A string encoded enum field does not match any of the known values */
  UNRECOGNIZED_ENUM_VALUE = 3,
  UNRECOGNIZED = -1,
}

export function parsingError_ErrorFromJSON(object: any): ParsingError_Error {
  switch (object) {
    case 0:
    case "UNKNOWN":
      return ParsingError_Error.UNKNOWN;
    case 1:
    case "MISSING_REQUIRED_FILED":
      return ParsingError_Error.MISSING_REQUIRED_FILED;
    case 2:
    case "FAILED_TO_PARSE_REQUIRED_VALUE":
      return ParsingError_Error.FAILED_TO_PARSE_REQUIRED_VALUE;
    case 3:
    case "UNRECOGNIZED_ENUM_VALUE":
      return ParsingError_Error.UNRECOGNIZED_ENUM_VALUE;
    case -1:
    case "UNRECOGNIZED":
    default:
      return ParsingError_Error.UNRECOGNIZED;
  }
}

export function parsingError_ErrorToJSON(object: ParsingError_Error): string {
  switch (object) {
    case ParsingError_Error.UNKNOWN:
      return "UNKNOWN";
    case ParsingError_Error.MISSING_REQUIRED_FILED:
      return "MISSING_REQUIRED_FILED";
    case ParsingError_Error.FAILED_TO_PARSE_REQUIRED_VALUE:
      return "FAILED_TO_PARSE_REQUIRED_VALUE";
    case ParsingError_Error.UNRECOGNIZED_ENUM_VALUE:
      return "UNRECOGNIZED_ENUM_VALUE";
    case ParsingError_Error.UNRECOGNIZED:
    default:
      return "UNRECOGNIZED";
  }
}

export interface ParsingWarning {
  warning: ParsingWarning_Warning;
  detail: string;
}

export enum ParsingWarning_Warning {
  UNKNOWN = 0,
  /** FAILED_TO_PARSE_OPTIONAL_VALUE - Unable to parse a value e.g. because the type does not match */
  FAILED_TO_PARSE_OPTIONAL_VALUE = 2,
  UNRECOGNIZED = -1,
}

export function parsingWarning_WarningFromJSON(object: any): ParsingWarning_Warning {
  switch (object) {
    case 0:
    case "UNKNOWN":
      return ParsingWarning_Warning.UNKNOWN;
    case 2:
    case "FAILED_TO_PARSE_OPTIONAL_VALUE":
      return ParsingWarning_Warning.FAILED_TO_PARSE_OPTIONAL_VALUE;
    case -1:
    case "UNRECOGNIZED":
    default:
      return ParsingWarning_Warning.UNRECOGNIZED;
  }
}

export function parsingWarning_WarningToJSON(object: ParsingWarning_Warning): string {
  switch (object) {
    case ParsingWarning_Warning.UNKNOWN:
      return "UNKNOWN";
    case ParsingWarning_Warning.FAILED_TO_PARSE_OPTIONAL_VALUE:
      return "FAILED_TO_PARSE_OPTIONAL_VALUE";
    case ParsingWarning_Warning.UNRECOGNIZED:
    default:
      return "UNRECOGNIZED";
  }
}

export interface ParsingResult {
  status: ParsingResult_Status;
  errors: ParsingError[];
  warnings: ParsingWarning[];
}

export enum ParsingResult_Status {
  UNKNOWN = 0,
  SUCCESS = 1,
  /** FAILED - Failed to parse. */
  FAILED = 2,
  /** FAILED_TO_FIND - Failed to find the geofence file. */
  FAILED_TO_FIND = 3,
  UNRECOGNIZED = -1,
}

export function parsingResult_StatusFromJSON(object: any): ParsingResult_Status {
  switch (object) {
    case 0:
    case "UNKNOWN":
      return ParsingResult_Status.UNKNOWN;
    case 1:
    case "SUCCESS":
      return ParsingResult_Status.SUCCESS;
    case 2:
    case "FAILED":
      return ParsingResult_Status.FAILED;
    case 3:
    case "FAILED_TO_FIND":
      return ParsingResult_Status.FAILED_TO_FIND;
    case -1:
    case "UNRECOGNIZED":
    default:
      return ParsingResult_Status.UNRECOGNIZED;
  }
}

export function parsingResult_StatusToJSON(object: ParsingResult_Status): string {
  switch (object) {
    case ParsingResult_Status.UNKNOWN:
      return "UNKNOWN";
    case ParsingResult_Status.SUCCESS:
      return "SUCCESS";
    case ParsingResult_Status.FAILED:
      return "FAILED";
    case ParsingResult_Status.FAILED_TO_FIND:
      return "FAILED_TO_FIND";
    case ParsingResult_Status.UNRECOGNIZED:
    default:
      return "UNRECOGNIZED";
  }
}

/**
 * Hypermast flag for communicating a vehicle's gefoence vertex limits. This is used in two places:
 * - The customer portal to prevent assigning a vehicle a site geofence that it cannot handle.
 * - SiteInfo cloud config pack generation to check the work of the customer portal.
 *   - If a vehicle ends up being assigned to a geofence with more vertices than this flag indicates
 *     is allowable, then the vehicle will be grounded using the `GROUNDING` cloud config pack (or
 *     cloud config generation will fail if the vehicle does not support grounding).
 */
export interface MaxSupportedGeofencePolygonVertices {
}

export enum MaxSupportedGeofencePolygonVertices_Enum {
  UNKNOWN = 0,
  /** SUPPORTS_1000 - First made available in Cayley */
  SUPPORTS_1000 = 1,
  UNRECOGNIZED = -1,
}

export function maxSupportedGeofencePolygonVertices_EnumFromJSON(
  object: any,
): MaxSupportedGeofencePolygonVertices_Enum {
  switch (object) {
    case 0:
    case "UNKNOWN":
      return MaxSupportedGeofencePolygonVertices_Enum.UNKNOWN;
    case 1:
    case "SUPPORTS_1000":
      return MaxSupportedGeofencePolygonVertices_Enum.SUPPORTS_1000;
    case -1:
    case "UNRECOGNIZED":
    default:
      return MaxSupportedGeofencePolygonVertices_Enum.UNRECOGNIZED;
  }
}

export function maxSupportedGeofencePolygonVertices_EnumToJSON(
  object: MaxSupportedGeofencePolygonVertices_Enum,
): string {
  switch (object) {
    case MaxSupportedGeofencePolygonVertices_Enum.UNKNOWN:
      return "UNKNOWN";
    case MaxSupportedGeofencePolygonVertices_Enum.SUPPORTS_1000:
      return "SUPPORTS_1000";
    case MaxSupportedGeofencePolygonVertices_Enum.UNRECOGNIZED:
    default:
      return "UNRECOGNIZED";
  }
}

function createBaseGeofence(): Geofence {
  return { utime: 0, shapes: [] };
}

export const Geofence = {
  encode(message: Geofence, writer: _m0.Writer = _m0.Writer.create()): _m0.Writer {
    if (message.utime !== 0) {
      writer.uint32(8).int64(message.utime);
    }
    for (const v of message.shapes) {
      GeofenceShape.encode(v!, writer.uint32(18).fork()).ldelim();
    }
    return writer;
  },

  decode(input: _m0.Reader | Uint8Array, length?: number): Geofence {
    const reader = input instanceof _m0.Reader ? input : _m0.Reader.create(input);
    let end = length === undefined ? reader.len : reader.pos + length;
    const message = createBaseGeofence();
    while (reader.pos < end) {
      const tag = reader.uint32();
      switch (tag >>> 3) {
        case 1:
          if (tag !== 8) {
            break;
          }

          message.utime = longToNumber(reader.int64() as Long);
          continue;
        case 2:
          if (tag !== 18) {
            break;
          }

          message.shapes.push(GeofenceShape.decode(reader, reader.uint32()));
          continue;
      }
      if ((tag & 7) === 4 || tag === 0) {
        break;
      }
      reader.skipType(tag & 7);
    }
    return message;
  },

  fromJSON(object: any): Geofence {
    return {
      utime: isSet(object.utime) ? globalThis.Number(object.utime) : 0,
      shapes: globalThis.Array.isArray(object?.shapes) ? object.shapes.map((e: any) => GeofenceShape.fromJSON(e)) : [],
    };
  },

  toJSON(message: Geofence): unknown {
    const obj: any = {};
    if (message.utime !== 0) {
      obj.utime = Math.round(message.utime);
    }
    if (message.shapes?.length) {
      obj.shapes = message.shapes.map((e) => GeofenceShape.toJSON(e));
    }
    return obj;
  },

  create<I extends Exact<DeepPartial<Geofence>, I>>(base?: I): Geofence {
    return Geofence.fromPartial(base ?? ({} as any));
  },
  fromPartial<I extends Exact<DeepPartial<Geofence>, I>>(object: I): Geofence {
    const message = createBaseGeofence();
    message.utime = object.utime ?? 0;
    message.shapes = object.shapes?.map((e) => GeofenceShape.fromPartial(e)) || [];
    return message;
  },
};

function createBaseGeofenceShape(): GeofenceShape {
  return { frame: 0, keepOut: false, source: 0, metadata: undefined, cylinder: undefined, poly2d: undefined };
}

export const GeofenceShape = {
  encode(message: GeofenceShape, writer: _m0.Writer = _m0.Writer.create()): _m0.Writer {
    if (message.frame !== 0) {
      writer.uint32(8).int32(message.frame);
    }
    if (message.keepOut === true) {
      writer.uint32(16).bool(message.keepOut);
    }
    if (message.source !== 0) {
      writer.uint32(24).int32(message.source);
    }
    if (message.metadata !== undefined) {
      Metadata.encode(message.metadata, writer.uint32(34).fork()).ldelim();
    }
    if (message.cylinder !== undefined) {
      Cylinder.encode(message.cylinder, writer.uint32(802).fork()).ldelim();
    }
    if (message.poly2d !== undefined) {
      Polygon.encode(message.poly2d, writer.uint32(810).fork()).ldelim();
    }
    return writer;
  },

  decode(input: _m0.Reader | Uint8Array, length?: number): GeofenceShape {
    const reader = input instanceof _m0.Reader ? input : _m0.Reader.create(input);
    let end = length === undefined ? reader.len : reader.pos + length;
    const message = createBaseGeofenceShape();
    while (reader.pos < end) {
      const tag = reader.uint32();
      switch (tag >>> 3) {
        case 1:
          if (tag !== 8) {
            break;
          }

          message.frame = reader.int32() as any;
          continue;
        case 2:
          if (tag !== 16) {
            break;
          }

          message.keepOut = reader.bool();
          continue;
        case 3:
          if (tag !== 24) {
            break;
          }

          message.source = reader.int32() as any;
          continue;
        case 4:
          if (tag !== 34) {
            break;
          }

          message.metadata = Metadata.decode(reader, reader.uint32());
          continue;
        case 100:
          if (tag !== 802) {
            break;
          }

          message.cylinder = Cylinder.decode(reader, reader.uint32());
          continue;
        case 101:
          if (tag !== 810) {
            break;
          }

          message.poly2d = Polygon.decode(reader, reader.uint32());
          continue;
      }
      if ((tag & 7) === 4 || tag === 0) {
        break;
      }
      reader.skipType(tag & 7);
    }
    return message;
  },

  fromJSON(object: any): GeofenceShape {
    return {
      frame: isSet(object.frame) ? frameType_EnumFromJSON(object.frame) : 0,
      keepOut: isSet(object.keepOut) ? globalThis.Boolean(object.keepOut) : false,
      source: isSet(object.source) ? geofenceShape_SourceFromJSON(object.source) : 0,
      metadata: isSet(object.metadata) ? Metadata.fromJSON(object.metadata) : undefined,
      cylinder: isSet(object.cylinder) ? Cylinder.fromJSON(object.cylinder) : undefined,
      poly2d: isSet(object.poly2d) ? Polygon.fromJSON(object.poly2d) : undefined,
    };
  },

  toJSON(message: GeofenceShape): unknown {
    const obj: any = {};
    if (message.frame !== 0) {
      obj.frame = frameType_EnumToJSON(message.frame);
    }
    if (message.keepOut === true) {
      obj.keepOut = message.keepOut;
    }
    if (message.source !== 0) {
      obj.source = geofenceShape_SourceToJSON(message.source);
    }
    if (message.metadata !== undefined) {
      obj.metadata = Metadata.toJSON(message.metadata);
    }
    if (message.cylinder !== undefined) {
      obj.cylinder = Cylinder.toJSON(message.cylinder);
    }
    if (message.poly2d !== undefined) {
      obj.poly2d = Polygon.toJSON(message.poly2d);
    }
    return obj;
  },

  create<I extends Exact<DeepPartial<GeofenceShape>, I>>(base?: I): GeofenceShape {
    return GeofenceShape.fromPartial(base ?? ({} as any));
  },
  fromPartial<I extends Exact<DeepPartial<GeofenceShape>, I>>(object: I): GeofenceShape {
    const message = createBaseGeofenceShape();
    message.frame = object.frame ?? 0;
    message.keepOut = object.keepOut ?? false;
    message.source = object.source ?? 0;
    message.metadata = (object.metadata !== undefined && object.metadata !== null)
      ? Metadata.fromPartial(object.metadata)
      : undefined;
    message.cylinder = (object.cylinder !== undefined && object.cylinder !== null)
      ? Cylinder.fromPartial(object.cylinder)
      : undefined;
    message.poly2d = (object.poly2d !== undefined && object.poly2d !== null)
      ? Polygon.fromPartial(object.poly2d)
      : undefined;
    return message;
  },
};

function createBaseMetadata(): Metadata {
  return { id: "", country: "" };
}

export const Metadata = {
  encode(message: Metadata, writer: _m0.Writer = _m0.Writer.create()): _m0.Writer {
    if (message.id !== "") {
      writer.uint32(10).string(message.id);
    }
    if (message.country !== "") {
      writer.uint32(18).string(message.country);
    }
    return writer;
  },

  decode(input: _m0.Reader | Uint8Array, length?: number): Metadata {
    const reader = input instanceof _m0.Reader ? input : _m0.Reader.create(input);
    let end = length === undefined ? reader.len : reader.pos + length;
    const message = createBaseMetadata();
    while (reader.pos < end) {
      const tag = reader.uint32();
      switch (tag >>> 3) {
        case 1:
          if (tag !== 10) {
            break;
          }

          message.id = reader.string();
          continue;
        case 2:
          if (tag !== 18) {
            break;
          }

          message.country = reader.string();
          continue;
      }
      if ((tag & 7) === 4 || tag === 0) {
        break;
      }
      reader.skipType(tag & 7);
    }
    return message;
  },

  fromJSON(object: any): Metadata {
    return {
      id: isSet(object.id) ? globalThis.String(object.id) : "",
      country: isSet(object.country) ? globalThis.String(object.country) : "",
    };
  },

  toJSON(message: Metadata): unknown {
    const obj: any = {};
    if (message.id !== "") {
      obj.id = message.id;
    }
    if (message.country !== "") {
      obj.country = message.country;
    }
    return obj;
  },

  create<I extends Exact<DeepPartial<Metadata>, I>>(base?: I): Metadata {
    return Metadata.fromPartial(base ?? ({} as any));
  },
  fromPartial<I extends Exact<DeepPartial<Metadata>, I>>(object: I): Metadata {
    const message = createBaseMetadata();
    message.id = object.id ?? "";
    message.country = object.country ?? "";
    return message;
  },
};

function createBaseHeightLimits(): HeightLimits {
  return { floor: 0, floorEnabled: false, ceiling: 0, ceilingEnabled: false, frame: 0 };
}

export const HeightLimits = {
  encode(message: HeightLimits, writer: _m0.Writer = _m0.Writer.create()): _m0.Writer {
    if (message.floor !== 0) {
      writer.uint32(13).float(message.floor);
    }
    if (message.floorEnabled === true) {
      writer.uint32(16).bool(message.floorEnabled);
    }
    if (message.ceiling !== 0) {
      writer.uint32(29).float(message.ceiling);
    }
    if (message.ceilingEnabled === true) {
      writer.uint32(32).bool(message.ceilingEnabled);
    }
    if (message.frame !== 0) {
      writer.uint32(40).int32(message.frame);
    }
    return writer;
  },

  decode(input: _m0.Reader | Uint8Array, length?: number): HeightLimits {
    const reader = input instanceof _m0.Reader ? input : _m0.Reader.create(input);
    let end = length === undefined ? reader.len : reader.pos + length;
    const message = createBaseHeightLimits();
    while (reader.pos < end) {
      const tag = reader.uint32();
      switch (tag >>> 3) {
        case 1:
          if (tag !== 13) {
            break;
          }

          message.floor = reader.float();
          continue;
        case 2:
          if (tag !== 16) {
            break;
          }

          message.floorEnabled = reader.bool();
          continue;
        case 3:
          if (tag !== 29) {
            break;
          }

          message.ceiling = reader.float();
          continue;
        case 4:
          if (tag !== 32) {
            break;
          }

          message.ceilingEnabled = reader.bool();
          continue;
        case 5:
          if (tag !== 40) {
            break;
          }

          message.frame = reader.int32() as any;
          continue;
      }
      if ((tag & 7) === 4 || tag === 0) {
        break;
      }
      reader.skipType(tag & 7);
    }
    return message;
  },

  fromJSON(object: any): HeightLimits {
    return {
      floor: isSet(object.floor) ? globalThis.Number(object.floor) : 0,
      floorEnabled: isSet(object.floorEnabled) ? globalThis.Boolean(object.floorEnabled) : false,
      ceiling: isSet(object.ceiling) ? globalThis.Number(object.ceiling) : 0,
      ceilingEnabled: isSet(object.ceilingEnabled) ? globalThis.Boolean(object.ceilingEnabled) : false,
      frame: isSet(object.frame) ? frameType_EnumFromJSON(object.frame) : 0,
    };
  },

  toJSON(message: HeightLimits): unknown {
    const obj: any = {};
    if (message.floor !== 0) {
      obj.floor = message.floor;
    }
    if (message.floorEnabled === true) {
      obj.floorEnabled = message.floorEnabled;
    }
    if (message.ceiling !== 0) {
      obj.ceiling = message.ceiling;
    }
    if (message.ceilingEnabled === true) {
      obj.ceilingEnabled = message.ceilingEnabled;
    }
    if (message.frame !== 0) {
      obj.frame = frameType_EnumToJSON(message.frame);
    }
    return obj;
  },

  create<I extends Exact<DeepPartial<HeightLimits>, I>>(base?: I): HeightLimits {
    return HeightLimits.fromPartial(base ?? ({} as any));
  },
  fromPartial<I extends Exact<DeepPartial<HeightLimits>, I>>(object: I): HeightLimits {
    const message = createBaseHeightLimits();
    message.floor = object.floor ?? 0;
    message.floorEnabled = object.floorEnabled ?? false;
    message.ceiling = object.ceiling ?? 0;
    message.ceilingEnabled = object.ceilingEnabled ?? false;
    message.frame = object.frame ?? 0;
    return message;
  },
};

function createBaseCylinder(): Cylinder {
  return { radius: 0, center: undefined, heightLimits: undefined };
}

export const Cylinder = {
  encode(message: Cylinder, writer: _m0.Writer = _m0.Writer.create()): _m0.Writer {
    if (message.radius !== 0) {
      writer.uint32(13).float(message.radius);
    }
    if (message.center !== undefined) {
      Vector2fPb.encode(message.center, writer.uint32(18).fork()).ldelim();
    }
    if (message.heightLimits !== undefined) {
      HeightLimits.encode(message.heightLimits, writer.uint32(26).fork()).ldelim();
    }
    return writer;
  },

  decode(input: _m0.Reader | Uint8Array, length?: number): Cylinder {
    const reader = input instanceof _m0.Reader ? input : _m0.Reader.create(input);
    let end = length === undefined ? reader.len : reader.pos + length;
    const message = createBaseCylinder();
    while (reader.pos < end) {
      const tag = reader.uint32();
      switch (tag >>> 3) {
        case 1:
          if (tag !== 13) {
            break;
          }

          message.radius = reader.float();
          continue;
        case 2:
          if (tag !== 18) {
            break;
          }

          message.center = Vector2fPb.decode(reader, reader.uint32());
          continue;
        case 3:
          if (tag !== 26) {
            break;
          }

          message.heightLimits = HeightLimits.decode(reader, reader.uint32());
          continue;
      }
      if ((tag & 7) === 4 || tag === 0) {
        break;
      }
      reader.skipType(tag & 7);
    }
    return message;
  },

  fromJSON(object: any): Cylinder {
    return {
      radius: isSet(object.radius) ? globalThis.Number(object.radius) : 0,
      center: isSet(object.center) ? Vector2fPb.fromJSON(object.center) : undefined,
      heightLimits: isSet(object.heightLimits) ? HeightLimits.fromJSON(object.heightLimits) : undefined,
    };
  },

  toJSON(message: Cylinder): unknown {
    const obj: any = {};
    if (message.radius !== 0) {
      obj.radius = message.radius;
    }
    if (message.center !== undefined) {
      obj.center = Vector2fPb.toJSON(message.center);
    }
    if (message.heightLimits !== undefined) {
      obj.heightLimits = HeightLimits.toJSON(message.heightLimits);
    }
    return obj;
  },

  create<I extends Exact<DeepPartial<Cylinder>, I>>(base?: I): Cylinder {
    return Cylinder.fromPartial(base ?? ({} as any));
  },
  fromPartial<I extends Exact<DeepPartial<Cylinder>, I>>(object: I): Cylinder {
    const message = createBaseCylinder();
    message.radius = object.radius ?? 0;
    message.center = (object.center !== undefined && object.center !== null)
      ? Vector2fPb.fromPartial(object.center)
      : undefined;
    message.heightLimits = (object.heightLimits !== undefined && object.heightLimits !== null)
      ? HeightLimits.fromPartial(object.heightLimits)
      : undefined;
    return message;
  },
};

function createBasePolygon(): Polygon {
  return { vertices: [], heightLimits: undefined };
}

export const Polygon = {
  encode(message: Polygon, writer: _m0.Writer = _m0.Writer.create()): _m0.Writer {
    for (const v of message.vertices) {
      Vector2fPb.encode(v!, writer.uint32(10).fork()).ldelim();
    }
    if (message.heightLimits !== undefined) {
      HeightLimits.encode(message.heightLimits, writer.uint32(26).fork()).ldelim();
    }
    return writer;
  },

  decode(input: _m0.Reader | Uint8Array, length?: number): Polygon {
    const reader = input instanceof _m0.Reader ? input : _m0.Reader.create(input);
    let end = length === undefined ? reader.len : reader.pos + length;
    const message = createBasePolygon();
    while (reader.pos < end) {
      const tag = reader.uint32();
      switch (tag >>> 3) {
        case 1:
          if (tag !== 10) {
            break;
          }

          message.vertices.push(Vector2fPb.decode(reader, reader.uint32()));
          continue;
        case 3:
          if (tag !== 26) {
            break;
          }

          message.heightLimits = HeightLimits.decode(reader, reader.uint32());
          continue;
      }
      if ((tag & 7) === 4 || tag === 0) {
        break;
      }
      reader.skipType(tag & 7);
    }
    return message;
  },

  fromJSON(object: any): Polygon {
    return {
      vertices: globalThis.Array.isArray(object?.vertices)
        ? object.vertices.map((e: any) => Vector2fPb.fromJSON(e))
        : [],
      heightLimits: isSet(object.heightLimits) ? HeightLimits.fromJSON(object.heightLimits) : undefined,
    };
  },

  toJSON(message: Polygon): unknown {
    const obj: any = {};
    if (message.vertices?.length) {
      obj.vertices = message.vertices.map((e) => Vector2fPb.toJSON(e));
    }
    if (message.heightLimits !== undefined) {
      obj.heightLimits = HeightLimits.toJSON(message.heightLimits);
    }
    return obj;
  },

  create<I extends Exact<DeepPartial<Polygon>, I>>(base?: I): Polygon {
    return Polygon.fromPartial(base ?? ({} as any));
  },
  fromPartial<I extends Exact<DeepPartial<Polygon>, I>>(object: I): Polygon {
    const message = createBasePolygon();
    message.vertices = object.vertices?.map((e) => Vector2fPb.fromPartial(e)) || [];
    message.heightLimits = (object.heightLimits !== undefined && object.heightLimits !== null)
      ? HeightLimits.fromPartial(object.heightLimits)
      : undefined;
    return message;
  },
};

function createBaseParsingError(): ParsingError {
  return { error: 0, detail: "" };
}

export const ParsingError = {
  encode(message: ParsingError, writer: _m0.Writer = _m0.Writer.create()): _m0.Writer {
    if (message.error !== 0) {
      writer.uint32(8).int32(message.error);
    }
    if (message.detail !== "") {
      writer.uint32(18).string(message.detail);
    }
    return writer;
  },

  decode(input: _m0.Reader | Uint8Array, length?: number): ParsingError {
    const reader = input instanceof _m0.Reader ? input : _m0.Reader.create(input);
    let end = length === undefined ? reader.len : reader.pos + length;
    const message = createBaseParsingError();
    while (reader.pos < end) {
      const tag = reader.uint32();
      switch (tag >>> 3) {
        case 1:
          if (tag !== 8) {
            break;
          }

          message.error = reader.int32() as any;
          continue;
        case 2:
          if (tag !== 18) {
            break;
          }

          message.detail = reader.string();
          continue;
      }
      if ((tag & 7) === 4 || tag === 0) {
        break;
      }
      reader.skipType(tag & 7);
    }
    return message;
  },

  fromJSON(object: any): ParsingError {
    return {
      error: isSet(object.error) ? parsingError_ErrorFromJSON(object.error) : 0,
      detail: isSet(object.detail) ? globalThis.String(object.detail) : "",
    };
  },

  toJSON(message: ParsingError): unknown {
    const obj: any = {};
    if (message.error !== 0) {
      obj.error = parsingError_ErrorToJSON(message.error);
    }
    if (message.detail !== "") {
      obj.detail = message.detail;
    }
    return obj;
  },

  create<I extends Exact<DeepPartial<ParsingError>, I>>(base?: I): ParsingError {
    return ParsingError.fromPartial(base ?? ({} as any));
  },
  fromPartial<I extends Exact<DeepPartial<ParsingError>, I>>(object: I): ParsingError {
    const message = createBaseParsingError();
    message.error = object.error ?? 0;
    message.detail = object.detail ?? "";
    return message;
  },
};

function createBaseParsingWarning(): ParsingWarning {
  return { warning: 0, detail: "" };
}

export const ParsingWarning = {
  encode(message: ParsingWarning, writer: _m0.Writer = _m0.Writer.create()): _m0.Writer {
    if (message.warning !== 0) {
      writer.uint32(8).int32(message.warning);
    }
    if (message.detail !== "") {
      writer.uint32(18).string(message.detail);
    }
    return writer;
  },

  decode(input: _m0.Reader | Uint8Array, length?: number): ParsingWarning {
    const reader = input instanceof _m0.Reader ? input : _m0.Reader.create(input);
    let end = length === undefined ? reader.len : reader.pos + length;
    const message = createBaseParsingWarning();
    while (reader.pos < end) {
      const tag = reader.uint32();
      switch (tag >>> 3) {
        case 1:
          if (tag !== 8) {
            break;
          }

          message.warning = reader.int32() as any;
          continue;
        case 2:
          if (tag !== 18) {
            break;
          }

          message.detail = reader.string();
          continue;
      }
      if ((tag & 7) === 4 || tag === 0) {
        break;
      }
      reader.skipType(tag & 7);
    }
    return message;
  },

  fromJSON(object: any): ParsingWarning {
    return {
      warning: isSet(object.warning) ? parsingWarning_WarningFromJSON(object.warning) : 0,
      detail: isSet(object.detail) ? globalThis.String(object.detail) : "",
    };
  },

  toJSON(message: ParsingWarning): unknown {
    const obj: any = {};
    if (message.warning !== 0) {
      obj.warning = parsingWarning_WarningToJSON(message.warning);
    }
    if (message.detail !== "") {
      obj.detail = message.detail;
    }
    return obj;
  },

  create<I extends Exact<DeepPartial<ParsingWarning>, I>>(base?: I): ParsingWarning {
    return ParsingWarning.fromPartial(base ?? ({} as any));
  },
  fromPartial<I extends Exact<DeepPartial<ParsingWarning>, I>>(object: I): ParsingWarning {
    const message = createBaseParsingWarning();
    message.warning = object.warning ?? 0;
    message.detail = object.detail ?? "";
    return message;
  },
};

function createBaseParsingResult(): ParsingResult {
  return { status: 0, errors: [], warnings: [] };
}

export const ParsingResult = {
  encode(message: ParsingResult, writer: _m0.Writer = _m0.Writer.create()): _m0.Writer {
    if (message.status !== 0) {
      writer.uint32(8).int32(message.status);
    }
    for (const v of message.errors) {
      ParsingError.encode(v!, writer.uint32(18).fork()).ldelim();
    }
    for (const v of message.warnings) {
      ParsingWarning.encode(v!, writer.uint32(26).fork()).ldelim();
    }
    return writer;
  },

  decode(input: _m0.Reader | Uint8Array, length?: number): ParsingResult {
    const reader = input instanceof _m0.Reader ? input : _m0.Reader.create(input);
    let end = length === undefined ? reader.len : reader.pos + length;
    const message = createBaseParsingResult();
    while (reader.pos < end) {
      const tag = reader.uint32();
      switch (tag >>> 3) {
        case 1:
          if (tag !== 8) {
            break;
          }

          message.status = reader.int32() as any;
          continue;
        case 2:
          if (tag !== 18) {
            break;
          }

          message.errors.push(ParsingError.decode(reader, reader.uint32()));
          continue;
        case 3:
          if (tag !== 26) {
            break;
          }

          message.warnings.push(ParsingWarning.decode(reader, reader.uint32()));
          continue;
      }
      if ((tag & 7) === 4 || tag === 0) {
        break;
      }
      reader.skipType(tag & 7);
    }
    return message;
  },

  fromJSON(object: any): ParsingResult {
    return {
      status: isSet(object.status) ? parsingResult_StatusFromJSON(object.status) : 0,
      errors: globalThis.Array.isArray(object?.errors) ? object.errors.map((e: any) => ParsingError.fromJSON(e)) : [],
      warnings: globalThis.Array.isArray(object?.warnings)
        ? object.warnings.map((e: any) => ParsingWarning.fromJSON(e))
        : [],
    };
  },

  toJSON(message: ParsingResult): unknown {
    const obj: any = {};
    if (message.status !== 0) {
      obj.status = parsingResult_StatusToJSON(message.status);
    }
    if (message.errors?.length) {
      obj.errors = message.errors.map((e) => ParsingError.toJSON(e));
    }
    if (message.warnings?.length) {
      obj.warnings = message.warnings.map((e) => ParsingWarning.toJSON(e));
    }
    return obj;
  },

  create<I extends Exact<DeepPartial<ParsingResult>, I>>(base?: I): ParsingResult {
    return ParsingResult.fromPartial(base ?? ({} as any));
  },
  fromPartial<I extends Exact<DeepPartial<ParsingResult>, I>>(object: I): ParsingResult {
    const message = createBaseParsingResult();
    message.status = object.status ?? 0;
    message.errors = object.errors?.map((e) => ParsingError.fromPartial(e)) || [];
    message.warnings = object.warnings?.map((e) => ParsingWarning.fromPartial(e)) || [];
    return message;
  },
};

function createBaseMaxSupportedGeofencePolygonVertices(): MaxSupportedGeofencePolygonVertices {
  return {};
}

export const MaxSupportedGeofencePolygonVertices = {
  encode(_: MaxSupportedGeofencePolygonVertices, writer: _m0.Writer = _m0.Writer.create()): _m0.Writer {
    return writer;
  },

  decode(input: _m0.Reader | Uint8Array, length?: number): MaxSupportedGeofencePolygonVertices {
    const reader = input instanceof _m0.Reader ? input : _m0.Reader.create(input);
    let end = length === undefined ? reader.len : reader.pos + length;
    const message = createBaseMaxSupportedGeofencePolygonVertices();
    while (reader.pos < end) {
      const tag = reader.uint32();
      switch (tag >>> 3) {
      }
      if ((tag & 7) === 4 || tag === 0) {
        break;
      }
      reader.skipType(tag & 7);
    }
    return message;
  },

  fromJSON(_: any): MaxSupportedGeofencePolygonVertices {
    return {};
  },

  toJSON(_: MaxSupportedGeofencePolygonVertices): unknown {
    const obj: any = {};
    return obj;
  },

  create<I extends Exact<DeepPartial<MaxSupportedGeofencePolygonVertices>, I>>(
    base?: I,
  ): MaxSupportedGeofencePolygonVertices {
    return MaxSupportedGeofencePolygonVertices.fromPartial(base ?? ({} as any));
  },
  fromPartial<I extends Exact<DeepPartial<MaxSupportedGeofencePolygonVertices>, I>>(
    _: I,
  ): MaxSupportedGeofencePolygonVertices {
    const message = createBaseMaxSupportedGeofencePolygonVertices();
    return message;
  },
};

type Builtin = Date | Function | Uint8Array | string | number | boolean | undefined;

export type DeepPartial<T> = T extends Builtin ? T
  : T extends globalThis.Array<infer U> ? globalThis.Array<DeepPartial<U>>
  : T extends ReadonlyArray<infer U> ? ReadonlyArray<DeepPartial<U>>
  : T extends {} ? { [K in keyof T]?: DeepPartial<T[K]> }
  : Partial<T>;

type KeysOfUnion<T> = T extends T ? keyof T : never;
export type Exact<P, I extends P> = P extends Builtin ? P
  : P & { [K in keyof P]: Exact<P[K], I[K]> } & { [K in Exclude<keyof I, KeysOfUnion<P>>]: never };

function longToNumber(long: Long): number {
  if (long.gt(globalThis.Number.MAX_SAFE_INTEGER)) {
    throw new globalThis.Error("Value is larger than Number.MAX_SAFE_INTEGER");
  }
  return long.toNumber();
}

if (_m0.util.Long !== Long) {
  _m0.util.Long = Long as any;
  _m0.configure();
}

function isSet(value: any): boolean {
  return value !== null && value !== undefined;
}
